import cn from 'classnames'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Tabs } from 'cng-common/components/tabs'
import { resetAllModifiedDataByKey } from 'cng-common/features/form'
import { Translation, useTranslatedText } from 'cng-common/features/translations'
import { TReplaceOptions } from 'cng-common/features/translations/components/translation/models/replaceOptions'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Tooltip } from 'core-components/library/tooltip'
import { BaseCrud } from 'core-components/types'
import { useCurrencyRestriction } from 'partner360/components/currenciesButtons/hooks/useCurrencyRestriction'
import { CURRENCY_TRANSLATION_KEYS } from 'partner360/components/currenciesButtons/translations'
import { KeyInitiative } from 'partner360/entities/keyInitiatives'

import { MDF_ACTIVITIES_TRANSLATION_KEYS } from '../../../../configs/translations'
import { parseSeparatedString } from '../../../../lib'
import { selectMdfActivitySavingIds } from '../../../../model'
import {
  IMdfActivity,
  IMdfActivityWithBusinessPriorities,
  TMdfAllocationEntityKey,
} from '../../../../model/types'
import { LinkedInitiativeCell } from '../../../mdfActivitiesTable/components/linkedInitiativeCell'
import { StatusBadge } from '../../../mdfActivitiesTable/components/statusBadge'
import { MdfActivityOutcomeFields } from '../../../mdfActivityOutcomeFields'
import {
  GENERAL_GROUP,
  IMdfFieldConfig,
  MDF_ACTIVITY_DETAILS_FORM_KEY,
  SECONDARY_GROUPS,
  WIDE_KINDS,
} from '../../config'
import {
  formatActivityDate,
  formatAmount,
  formatKeyInitiativeSuggestions,
  getAllocationBreakdown,
  getEmptyMdfActivityOutcomeForm,
  getInitialFormData,
  TMdfDetailsForm,
} from '../../helpers'
import { BadgeField } from '../badgeField'

import styles from './MdfActivityDetailsContent.module.scss'

const KEYS = MDF_ACTIVITIES_TRANSLATION_KEYS.detailsModal

const INITIATIVE_ICON = 'kbase kbase-star'
const OUTCOME_ICON = 'kbase kbase-trophy'

const EMPTY = <span className={styles.empty}>—</span>

const textOrEmpty = (value: string | null | undefined): ReactNode =>
  value && value.trim() ? value : EMPTY

const renderChips = (values: string[]): ReactNode =>
  values.length ? (
    <div className={styles.chips}>
      {values.map((value, index) => (
        <span key={`${value}-${index}`} className={styles.chip}>
          {value}
        </span>
      ))}
    </div>
  ) : (
    EMPTY
  )

const renderReadOnlyValue = (
  activity: IMdfActivityWithBusinessPriorities,
  cfg: IMdfFieldConfig
): ReactNode => {
  const raw = activity[cfg.field]

  switch (cfg.kind) {
    case 'currency': {
      const formatted = formatAmount(raw as string | number, /Usd$/.test(cfg.field))
      return formatted || EMPTY
    }
    case 'percent':
      return raw === null || raw === undefined || raw === '' ? EMPTY : <>{`${raw}%`}</>
    case 'date': {
      const formatted = formatActivityDate(raw as string)
      return formatted || EMPTY
    }
    case 'list':
    case 'emails':
      return renderChips((raw as string[]) ?? [])
    case 'suggestions': {
      const formatted = formatKeyInitiativeSuggestions(activity.keyInitiativeSuggestions)
      return formatted || EMPTY
    }
    default: {
      const text = raw === null || raw === undefined || raw === '' ? '' : String(raw)
      return text || EMPTY
    }
  }
}

/**
 * General-section fields whose badges also appear as a column of the activity's
 * allocation split, so their per-value amount/share can be expanded underneath.
 */
const BADGE_FIELD_TO_ALLOCATION_KEY = {
  strategicInitiatives: 'strategicInitiative',
  businessUnit: 'businessUnit',
  category: 'category',
} as const satisfies Partial<Record<IMdfFieldConfig['field'], TMdfAllocationEntityKey>>

type TBadgeField = keyof typeof BADGE_FIELD_TO_ALLOCATION_KEY

const isBadgeField = (field: IMdfFieldConfig['field']): field is TBadgeField =>
  field in BADGE_FIELD_TO_ALLOCATION_KEY

const getBadgeValues = (
  activity: IMdfActivityWithBusinessPriorities,
  field: TBadgeField
): string[] =>
  // `category` is still the raw `;`-separated API string, unlike the other legacy lists.
  field === 'category' ? parseSeparatedString(activity.category) : (activity[field] ?? [])

interface IBreakdownToggle {
  isExpanded: boolean
  onToggle: () => void
}

/** General section overrides: status, business unit, category and initiatives render as badges. */
const renderGeneralValue = (
  activity: IMdfActivityWithBusinessPriorities,
  cfg: IMdfFieldConfig,
  breakdownToggle: IBreakdownToggle
): ReactNode => {
  if (cfg.field === 'activityStatus') {
    return <StatusBadge status={activity.activityStatus} />
  }

  if (isBadgeField(cfg.field)) {
    return (
      <BadgeField
        values={getBadgeValues(activity, cfg.field)}
        breakdown={getAllocationBreakdown(
          activity.strategicInitiativeAllocations,
          BADGE_FIELD_TO_ALLOCATION_KEY[cfg.field]
        )}
        currencyCode={activity.localCurrency}
        isExpanded={breakdownToggle.isExpanded}
        onToggle={breakdownToggle.onToggle}
      />
    )
  }

  return renderReadOnlyValue(activity, cfg)
}

interface ISectionCardProps {
  icon: string
  titleKey: string
  headerAction?: ReactNode
  children: ReactNode
}

const SectionCard = ({ icon, titleKey, headerAction, children }: ISectionCardProps) => (
  <section className={styles.card}>
    <header className={styles.cardHeader}>
      <span className={styles.cardIcon}>
        <i className={icon} aria-hidden="true" />
      </span>
      <span className={styles.cardTitle}>
        <Translation text={titleKey} span />
      </span>
      {headerAction ? <div className={styles.cardHeaderAction}>{headerAction}</div> : null}
    </header>
    <div className={styles.grid}>{children}</div>
  </section>
)

interface IFieldProps {
  labelKey?: string
  labelParams?: TReplaceOptions[]
  wide?: boolean
  /**
   * Lets the cell share the section grid's rows, so that label, badges and the
   * expanded breakdown line up across the neighbouring badge fields.
   */
  rowAligned?: boolean
  children: ReactNode
}

const Field = ({ labelKey, labelParams, wide, rowAligned, children }: IFieldProps) => (
  <div className={cn(styles.cell, { [styles.wide]: wide, [styles.rowAligned]: rowAligned })}>
    {labelKey && (
      <span className={styles.label}>
        <Translation text={labelKey} replaceParams={labelParams} span />
      </span>
    )}
    <div className={styles.value}>{children}</div>
  </div>
)

export interface IMdfActivityDetailsContentProps {
  activity: IMdfActivityWithBusinessPriorities | null
  initiative?: KeyInitiative | null
  /**
   * CRUD source of available P360 key initiatives for the linked-initiative dropdown.
   */
  initiativesSource: BaseCrud<any>
  /**
   * Lookup of P360 key initiative id → name, used to resolve suggestion labels.
   */
  initiativesById: Map<number, string>
  /** Focus the Outcome item editor when opened via the outcome button. */
  focusOutcome?: boolean
  isReadOnly?: boolean
  onLink: (activity: IMdfActivity, keyInitiativeId: number | null) => void
  onCreateInitiative: (activity: IMdfActivity, value: string) => void
}

export const MdfActivityDetailsContent = ({
  activity,
  initiative,
  initiativesSource,
  initiativesById,
  focusOutcome,
  isReadOnly,
  onLink,
  onCreateInitiative,
}: IMdfActivityDetailsContentProps) => {
  const dispatch = useAppDispatch()
  const savingIds = useSelector(selectMdfActivitySavingIds)
  const [selectedCurrency, setCurrency] = useState<'usd' | 'lc'>('usd')
  const changeCurrency = useCallback(
    (value: string) => setCurrency(value === 'USD' ? 'usd' : 'lc'),
    []
  )
  const currencyRestriction = useCurrencyRestriction(
    selectedCurrency === 'usd' ? 'USD' : 'LC',
    changeCurrency
  )
  const currency = currencyRestriction.currency === 'USD' ? 'usd' : 'lc'
  const currencyControl = (
    <SegmentedControl
      selectedItem={currency === 'usd' ? 0 : 1}
      size="small"
      isDisabled={currencyRestriction.isMultiLocalCurrencyLocked}
      onItemSelect={(index) => currencyRestriction.changeCurrency(index === 0 ? 'USD' : 'LC')}
    >
      <SegmentedControl.Item>{KEYS.currency.usd}</SegmentedControl.Item>
      {activity?.localCurrency?.toUpperCase() !== 'USD' && (
        <SegmentedControl.Item>
          {activity?.localCurrency || KEYS.currency.local}
        </SegmentedControl.Item>
      )}
    </SegmentedControl>
  )
  const [activeTab, setActiveTab] = useState(0)
  const [isBreakdownExpanded, setIsBreakdownExpanded] = useState(false)
  const [localCurrencyFallback] = useTranslatedText(KEYS.currency.local)
  const initialForm = useMemo<TMdfDetailsForm>(
    () => (activity ? getInitialFormData(activity) : getEmptyMdfActivityOutcomeForm()),
    [activity]
  )

  useEffect(() => {
    return () => {
      dispatch(resetAllModifiedDataByKey(MDF_ACTIVITY_DETAILS_FORM_KEY))
    }
  }, [dispatch])

  if (!activity) {
    return <></>
  }

  const breakdownToggle: IBreakdownToggle = {
    isExpanded: isBreakdownExpanded,
    onToggle: () => setIsBreakdownExpanded((expanded) => !expanded),
  }

  const isSaving = savingIds.includes(activity.entryId)
  const businessPriorities = activity.businessPriorities ?? []

  return (
    <div className={styles.root}>
      <Tabs
        selectedItem={activeTab}
        onItemSelect={setActiveTab}
        keepMounted
        classes={{
          header: styles.tabsHeader,
          items: styles.tabsItems,
          item: styles.tabItem,
          active: styles.tabItemActive,
          content: styles.tabsContent,
        }}
      >
        <Tabs.Item title={KEYS.tabs.details}>
          <div className={styles.sections}>
            <SectionCard icon={GENERAL_GROUP.icon} titleKey={GENERAL_GROUP.titleKey}>
              {GENERAL_GROUP.fields.map((cfg) => (
                <Field key={cfg.field} labelKey={cfg.labelKey} rowAligned={isBadgeField(cfg.field)}>
                  {renderGeneralValue(activity, cfg, breakdownToggle)}
                </Field>
              ))}
            </SectionCard>

            <SectionCard icon={INITIATIVE_ICON} titleKey={KEYS.groups.initiative}>
              <Field labelKey={KEYS.fields.initiativeName}>
                <LinkedInitiativeCell
                  activity={activity}
                  initiativesSource={initiativesSource}
                  initiativesById={initiativesById}
                  isSaving={isSaving}
                  isDisabled={isReadOnly}
                  onLink={onLink}
                  onCreateInitiative={onCreateInitiative}
                />
              </Field>
              <Field labelKey={KEYS.fields.initiativeStrategicInitiative}>
                {textOrEmpty(initiative?.Development)}
              </Field>
              <Field labelKey={KEYS.fields.businessPriority}>
                {businessPriorities.length ? businessPriorities.join(', ') : EMPTY}
              </Field>
              <Field labelKey={KEYS.fields.initiativeDescription} wide>
                {textOrEmpty(initiative?.Description)}
              </Field>
            </SectionCard>

            <SectionCard icon={OUTCOME_ICON} titleKey={KEYS.groups.outcome}>
              <div className={styles.outcomeFields}>
                <MdfActivityOutcomeFields
                  formKey={MDF_ACTIVITY_DETAILS_FORM_KEY}
                  initialData={initialForm}
                  autoFocusOutcome={focusOutcome}
                  isDisabled={isReadOnly}
                />
              </div>
            </SectionCard>
          </div>
        </Tabs.Item>

        <Tabs.Item title={KEYS.tabs.financialsContacts}>
          <div className={styles.sections}>
            {SECONDARY_GROUPS.map((group) => {
              const hasCurrencyFields = group.fields.some((f) => f.currencyVariant)
              const visibleFields = group.fields.filter(
                (cfg) => !cfg.currencyVariant || cfg.currencyVariant === currency
              )

              return (
                <SectionCard
                  key={group.titleKey}
                  icon={group.icon}
                  titleKey={group.titleKey}
                  headerAction={
                    hasCurrencyFields ? (
                      currencyRestriction.isMultiLocalCurrencyLocked ? (
                        <Tooltip
                          inline
                          tipContent={() => (
                            <Translation text={CURRENCY_TRANSLATION_KEYS.multipleLocalCurrencies} />
                          )}
                        >
                          {currencyControl}
                        </Tooltip>
                      ) : (
                        currencyControl
                      )
                    ) : undefined
                  }
                >
                  {visibleFields.map((cfg) => (
                    <Field
                      key={cfg.field}
                      labelKey={cfg.labelKey}
                      labelParams={
                        cfg.currencyVariant
                          ? [
                              {
                                key: '{currency}',
                                value:
                                  cfg.currencyVariant === 'usd'
                                    ? 'USD'
                                    : activity.localCurrency || localCurrencyFallback,
                              },
                            ]
                          : undefined
                      }
                      wide={WIDE_KINDS.includes(cfg.kind)}
                    >
                      {renderReadOnlyValue(activity, cfg)}
                    </Field>
                  ))}
                </SectionCard>
              )
            })}
          </div>
        </Tabs.Item>
      </Tabs>
    </div>
  )
}

MdfActivityDetailsContent.displayName = 'MdfActivityDetailsContent'
